import { MEMBER_ID, StringTMap } from '@@types/common';
import Member from '@@types/Member';
import { PositionRef } from '@@types/Position';

import { MemberStatus } from '~/constants';
import { DAY } from '~/constants/date';
import GA from '~/services/GA/GA';
import { api } from '~/stores/providers/Api2';
import logging from '~/stores/providers/logging';
import { rename } from '~/utils/fnUtil/fnUtil';
import { getMembersFullName, isDormant, isLeaver } from '~/utils/memberUtil';
import { encaseP } from 'fluture';
import { assignWith, isEmpty, isEqual } from 'lodash';
import { action, computed, observable, runInAction } from 'mobx';
import { assoc, map, mergeDeepRight, mergeRight, pipe, pluck, tap } from 'ramda';
import values from 'ramda/es/values';
import { Maybe } from 'ramda-fantasy';

class MembersStore {
  @observable.shallow
  members = {};

  memberFetchedAt: StringTMap<number> = {};

  @observable.shallow
  membersStatus = {};

  memberStatusRequest = null;

  @observable
  positionMap = {};

  @observable
  rankMap = {};

  statusDelay = {};

  @computed
  get allChannelMemberIds() {
    return pipe<any, any, any>(values, pluck('id'))(this.members);
  }

  getById = (memberId) => {
    return this.members[memberId];
  };

  getStatusById = (id) => {
    const member = this.getById(id) || {};
    if (isLeaver(member) || isDormant(member)) {
      return MemberStatus.Offline;
    }

    return this.membersStatus[id] || MemberStatus.Offline;
  };

  getMembersById = (membersId) => {
    return map(this.getById, membersId);
  };

  safeGetById = (memberId): Maybe<Member> => {
    const member = this.getById(memberId);
    return isEmpty(member) ? Maybe.Nothing() : Maybe.Just(member);
  };

  public isMemberCacheExpired = (memberId: MEMBER_ID, now: number) => {
    const fetchedAt = this.memberFetchedAt[memberId];
    return fetchedAt && now - fetchedAt > DAY;
  };

  updateFetchedAtList = (members: Member[]) => {
    const now = Date.now();
    members.forEach((member) => {
      this.memberFetchedAt[member.id] = now;
    });
  };

  search = (keyword, boostMembersId) => {
    return pipe<any, any, any, any>(
      encaseP(api.member.searchMembers),
      map(pluck('member')),
      map(tap(this.mergeMembers))
    )({ keyword, boostMembersId });
  };

  verifyEqualEmailAddress = (member?: Member) => {
    const prevMember = this.members[member.id];
    if (!member || !member.id || !member.emailAddress || !prevMember || !prevMember.emailAddress) {
      return;
    }

    if (prevMember.emailAddress !== member.emailAddress) {
      try {
        throw new Error();
      } catch (e) {
        GA.writeDiffEmailError(e, `${prevMember.emailAddress} => ${member.emailAddress}`);
      }
    }
  };

  @action
  setMember = (member) => {
    this.verifyEqualEmailAddress(member);
    this.members = assoc(member.id, member, this.members);
    return this.members[member.id];
  };

  @action
  setPosition = (position: PositionRef): void => {
    this.positionMap[position.id] = mergeRight(this.positionMap[position.id], position);
  };

  @action
  mergePositionMap = (positionMap: StringTMap<PositionRef>) => {
    this.positionMap = mergeRight(this.positionMap, positionMap);
  };

  @action
  setRank = (rank: PositionRef) => {
    this.rankMap[rank.id] = mergeRight(this.rankMap[rank.id], rank);
  };

  @action
  mergeRankMap = (rankMap: StringTMap<PositionRef>) => {
    this.rankMap = mergeRight(this.rankMap, rankMap);
  };

  @action
  clear = () => {
    this.members = {};
    this.memberFetchedAt = {};
  };

  @action
  mergeMember = (member) => {
    if (this.getById(member.id)) {
      this.updateMember(member);
    } else {
      this.setMember(member);
    }
    return member;
  };

  @action
  mergeMembers = (members = []) => {
    runInAction(() => {
      members.forEach((member) => {
        this.verifyEqualEmailAddress(member);
        this.members[member.id] = !isEmpty(this.members[member.id])
          ? assignWith(this.members[member.id], member, (val, other) => other || val)
          : member;
      });
    });
    return members;
  };

  @action
  setMembers = (members) => {
    this.members = members;
    return this.members;
  };

  updateTimezone = (timezoneName) => {
    return api.me.updateUser({ timezoneName });
  };

  @action
  updateMember = (member) => {
    this.verifyEqualEmailAddress(member);
    this.members[member.id] = mergeDeepRight(this.members[member.id], member);
  };

  @action
  updateStatus = ({ id, status }) => {
    this.membersStatus[id] = status;
  };

  updateMembersStatus = (membersStatus) => {
    runInAction(() => {
      pipe<any, any, any>(map(rename({ memberId: 'id', memberStatus: 'status' })), this.updateStatus)(membersStatus);
    });
  };

  @action
  cacheMemberStatusRequest = (body) => {
    logging.debug('cacheMemberStatus:', body.content);
    this.memberStatusRequest = body;
    return body;
  };

  @action
  onResponseMemberStatus = (body) => {
    logging.debug('onResponseMemberStatus:', body.content);
    if (isEqual(this.memberStatusRequest, body)) {
      this.memberStatusRequest = null;
    }
  };

  getMemberNameNicknames = (memberIds: string[]) => {
    return pipe(map(this.getById), getMembersFullName)(memberIds);
  };
}

export default MembersStore;

export const membersStore = new MembersStore();
